/**
 * Webtech HR Engine™ - AI Credits Indicator
 * Affiche le solde de crédits IA dans le header et alerte en cas de solde faible
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Sparkles,
  Coins,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  History,
  Zap,
  X,
  ShoppingCart,
  Plus
} from 'lucide-react';

// Seuil de crédits faibles
const LOW_CREDIT_THRESHOLD = 10;

// Délai minimum entre 2 alertes de crédits faibles (12h en ms)
const LOW_CREDIT_ALERT_COOLDOWN_MS = 12 * 60 * 60 * 1000;

// Clé localStorage pour mémoriser la dernière alerte par société
const getLowCreditStorageKey = (companyId) => `ai_credits_low_alert_${companyId || 'global'}`;

// Format number with spaces
const formatNumber = (num) => {
  return new Intl.NumberFormat('fr-FR').format(num || 0);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function AICreditsIndicator() {
  const { user } = useAuth();
  const { isPro, isSuperAdmin } = usePlan();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [costs, setCosts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasShownLowCreditAlert, setHasShownLowCreditAlert] = useState(false);
  const dropdownRef = useRef(null);
  const prevBalanceRef = useRef(null);

  // Ne pas afficher pour les SuperAdmins ou les plans FREE
  const shouldShow = isPro && !isSuperAdmin && user?.companyId;

  // Fetch balance on mount and periodically
  useEffect(() => {
    if (shouldShow) {
      fetchBalance();
      // Poll every 60 seconds
      const interval = setInterval(fetchBalance, 60000);
      return () => clearInterval(interval);
    }
  }, [shouldShow, user?.companyId]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Alert when credits are low - avec cooldown persistant (localStorage) pour éviter le spam
  useEffect(() => {
    if (balance === null) return;

    const storageKey = getLowCreditStorageKey(user?.companyId);

    // Si le solde est revenu au-dessus du seuil, on réinitialise pour pouvoir alerter si re-baisse
    if (balance >= LOW_CREDIT_THRESHOLD) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        // Ignore quota / privacy errors
      }
      setHasShownLowCreditAlert(false);
      prevBalanceRef.current = balance;
      return;
    }

    // Solde faible : vérifier si on a déjà alerté récemment
    if (hasShownLowCreditAlert) {
      prevBalanceRef.current = balance;
      return;
    }

    let lastAlertAt = 0;
    try {
      const stored = localStorage.getItem(storageKey);
      lastAlertAt = stored ? parseInt(stored, 10) || 0 : 0;
    } catch (e) {
      lastAlertAt = 0;
    }

    const now = Date.now();
    const cooldownExpired = now - lastAlertAt > LOW_CREDIT_ALERT_COOLDOWN_MS;

    if (cooldownExpired) {
      toast.warning(
        `Crédits IA faibles : ${balance} restants. Contactez votre administrateur.`,
        {
          duration: 8000,
          id: 'ai-credits-low', // ID fixe pour éviter les doublons simultanés
        }
      );
      try {
        localStorage.setItem(storageKey, String(now));
      } catch (e) {
        // Ignore
      }
      // Notification DB uniquement si on passe sous le seuil (transition), pas à chaque refresh
      if (prevBalanceRef.current === null || prevBalanceRef.current >= LOW_CREDIT_THRESHOLD) {
        createLowCreditNotification();
      }
    }

    setHasShownLowCreditAlert(true);
    prevBalanceRef.current = balance;
  }, [balance, user?.companyId]);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/ai/credits/balance');
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching AI credits balance:', error);
    }
  };

  const fetchCosts = async () => {
    try {
      const response = await api.get('/ai/credits/costs');
      setCosts(response.data.costs || []);
    } catch (error) {
      console.error('Error fetching AI costs:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/ai/credits/history');
      setHistory(response.data.transactions?.slice(0, 5) || []);
    } catch (error) {
      console.error('Error fetching AI credits history:', error);
    }
  };

  const createLowCreditNotification = async () => {
    try {
      // Create notification for admins about low credits
      await api.post('/notifications/ai-credits-low', {
        balance: balance,
        threshold: LOW_CREDIT_THRESHOLD
      });
    } catch (error) {
      // Silent fail - notification endpoint may not exist
      console.log('Low credit notification not sent:', error.message);
    }
  };

  const handleOpen = async () => {
    if (!isOpen) {
      setLoading(true);
      await Promise.all([fetchCosts(), fetchHistory()]);
      setLoading(false);
    }
    setIsOpen(!isOpen);
  };

  // Don't render if not applicable
  if (!shouldShow) {
    return null;
  }

  // Determine badge color based on balance
  const getBadgeStyle = () => {
    if (balance === null) return 'bg-slate-100 text-slate-600';
    if (balance < LOW_CREDIT_THRESHOLD) return 'bg-red-100 text-red-700 animate-pulse';
    if (balance < 30) return 'bg-amber-100 text-amber-700';
    return 'bg-purple-100 text-purple-700';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Credits Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleOpen}
        className={`relative flex items-center gap-1.5 h-9 px-2 sm:px-3 ${
          balance !== null && balance < LOW_CREDIT_THRESHOLD 
            ? 'text-red-600 hover:text-red-700' 
            : 'text-slate-600 hover:text-slate-900'
        }`}
        data-testid="ai-credits-indicator"
      >
        <Sparkles className="w-4 h-4" />
        <Badge className={`${getBadgeStyle()} text-xs font-medium`}>
          {balance !== null ? formatNumber(balance) : '...'}
        </Badge>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        
        {/* Low credits warning dot */}
        {balance !== null && balance < LOW_CREDIT_THRESHOLD && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />
        )}
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                <span className="font-semibold">Crédits IA</span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-3xl font-bold">{formatNumber(balance)}</span>
              <span className="text-white/80 text-sm">crédits disponibles</span>
            </div>
            
            {/* Progress bar for low credits */}
            {balance !== null && balance < 50 && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-white/80 mb-1">
                  <span>Solde</span>
                  <span>{balance < LOW_CREDIT_THRESHOLD ? '⚠️ Critique' : 'Faible'}</span>
                </div>
                <Progress 
                  value={Math.min((balance / 100) * 100, 100)} 
                  className="h-1.5 bg-white/20"
                />
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <Sparkles className="w-6 h-6 text-purple-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-slate-500">Chargement...</p>
            </div>
          ) : (
            <>
              {/* AI Actions & Costs */}
              <div className="p-4 border-b border-slate-100">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Coût des actions IA
                </h4>
                <div className="space-y-1.5">
                  {costs.slice(0, 4).map((cost) => (
                    <div 
                      key={cost.actionId}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-slate-600 truncate">{cost.actionName}</span>
                      <Badge variant="outline" className="text-xs">
                        {cost.creditCost} crédits
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent History */}
              <div className="p-4">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <History className="w-3 h-3" />
                  Historique récent
                </h4>
                {history.length > 0 ? (
                  <div className="space-y-2">
                    {history.map((tx) => (
                      <div 
                        key={tx.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <TrendingDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                          <span className="text-slate-600 truncate text-xs">
                            {tx.description}
                          </span>
                        </div>
                        <span className="text-red-600 font-medium text-xs whitespace-nowrap">
                          {tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-2">
                    Aucune consommation récente
                  </p>
                )}
              </div>

              {/* Low credits warning */}
              {balance !== null && balance < LOW_CREDIT_THRESHOLD && (
                <div className="p-4 bg-red-50 border-t border-red-100">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Crédits insuffisants</p>
                      <p className="text-xs text-red-600 mt-0.5">
                        Rechargez vos crédits IA pour continuer à utiliser les fonctionnalités.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buy Credits Button */}
              {user?.role === 'Admin' && (
                <div className="p-3 border-t border-slate-100">
                  <Button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/billing/credits');
                    }}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    data-testid="buy-credits-btn"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Acheter des crédits
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
